import {
  DIVINE_SMITE_FEATURE_ID, SEARING_SMITE_SPELL_ID, SHINING_SMITE_SPELL_ID, canCastDivineSmite, canCastSearingSmite, canCastShiningSmite, divineSmiteDice,
  hasPaladinFeature, isFiendOrUndead, isSmiteAttack, paladinsSmiteFreeRemaining, searingSmiteDice, shiningSmiteDice,
} from '../../../../shared/rules/paladin.mjs';

const LABEL = 'Divine Smite';
// Smite spells the player prepared, cast with a slot of at least their level. Divine Smite (with its free use) is separate.
const SPELL_SMITES = Object.freeze([
  { key: 'searing', spellId: SEARING_SMITE_SPELL_ID, name: 'Searing Smite', level: 1, sides: 6, type: 'fire', typeLabel: 'Fire', dice: searingSmiteDice,
    known: canCastSearingSmite, note: 'Fire · the target keeps burning' },
  { key: 'shining', spellId: SHINING_SMITE_SPELL_ID, name: 'Shining Smite', level: 2, sides: 6, type: 'radiant', typeLabel: 'Radiant', dice: shiningSmiteDice,
    known: canCastShiningSmite, note: 'Radiant · the target glows; attacks against it have Advantage' },
]);
const ineligible = (reasonCode) => ({ status: 'ineligible', featureId: DIVINE_SMITE_FEATURE_ID, featureLabel: LABEL, reasonCode });
const component = (label, count, sides, value) => [{
  id: DIVINE_SMITE_FEATURE_ID,
  label,
  source: { type: 'spell', featureId: DIVINE_SMITE_FEATURE_ID },
  dice: { count, sides },
  flat: 0,
  damageType: { mode: 'explicit', value },
  critical: { dice: 'double' },
}];
const levels = (slots) => Object.entries(slots || {}).filter(([, count]) => Number(count) > 0).map(([level]) => Number(level)).filter(Number.isInteger).sort((a, b) => a - b);

/**
 * 2024 smite spells after a Melee weapon or Unarmed Strike hit on your turn, each a Bonus Action:
 * - Divine Smite: a spell slot, or the free use from Paladin's Smite, for 2d8 Radiant (+1d8 per slot level above 1,
 *   +1d8 against a Fiend or Undead);
 * - Searing Smite (when prepared): a slot for 1d6 Fire (+1d6 per slot level above 1), and the target starts burning.
 * Dice double on a critical hit. One decision lists both, since the Bonus Action allows only one. The server spends
 * the choice in its claim.
 */
export const evaluateDivineSmite = ({ context, queries = {} }) => {
  const character = context?.attacker?.entity;
  const attackerId = context?.attacker?.combatantId;
  const divine = canCastDivineSmite(character);
  const spellSmites = SPELL_SMITES.filter((smite) => smite.known(character));
  if (!divine && !spellSmites.length) return ineligible('no-divine-smite');
  if (context?.roll?.hit === false) return ineligible('not-a-hit');
  if (!isSmiteAttack(context?.attack)) return ineligible('not-melee-hit');
  const combat = context?.combat || {};
  if (!combat.campaignId || !Number.isInteger(combat.turnSequence) || combat.activeCombatantId !== attackerId) return ineligible('not-own-turn');
  const ledger = context.combatState?.turnActionLedger;
  const current = ledger?.combatantId === attackerId && ledger.turnSequence === combat.turnSequence ? ledger : null;
  if (current?.bonusActionSpent) return ineligible('bonus-action-spent');

  const fiendOrUndead = isFiendOrUndead(context.target?.entity);
  const dice = (slotLevel) => divineSmiteDice({ slotLevel, fiendOrUndead });
  const slots = [
    ...levels(queries.availableSlots?.regular).map((level) => ({ pool: 'spellcasting', level, name: `Level ${level} slot` })),
    ...levels(queries.availableSlots?.warlock).map((level) => ({ pool: 'pactMagic', level, name: `Pact slot (level ${level})` })),
  ];
  const choices = [];
  if (divine && hasPaladinFeature(character, 'paladinsSmite') && paladinsSmiteFreeRemaining(character) > 0) {
    choices.push({ id: 'free', action: 'apply', label: `Free (Paladin's Smite) · ${dice(1)}d8`, spell: 'divine', use: { use: 'free', slotLevel: 1 } });
  }
  // Only one spell slot can be expended per turn; the free use is not a slot.
  if (!current?.spellSlotSpent) {
    if (divine) slots.forEach(({ pool, level, name }) => choices.push({ id: `slot:${pool}:${level}`, action: 'apply',
      label: `${name} · ${dice(level)}d8`, spell: 'divine', use: { use: 'slot', pool, slotLevel: level } }));
    spellSmites.forEach((smite) => slots.filter(({ level }) => level >= smite.level).forEach(({ pool, level, name }) => choices.push({ id: `${smite.key}:${pool}:${level}`,
      action: 'apply', label: `${smite.name} · ${name} · ${smite.dice(level)}d${smite.sides} ${smite.typeLabel}`, spell: smite, use: { use: 'slot', pool, slotLevel: level } })));
  }
  if (!choices.length) return ineligible(current?.spellSlotSpent ? 'spell-slot-spent-this-turn' : 'no-smite-resource');

  // The prompt's grid: each smite spell is a row and each slot level a column (every level up to the highest one with a
  // slot left, used-up levels shown dim), so a cell is one slot choice. The free use and No Smite stay buttons.
  const regular = queries.availableSlots?.regular || {};
  const highest = Math.max(0, ...levels(regular));
  const columns = [
    ...Array.from({ length: highest }, (_, index) => index + 1).map((level) => ({ key: `spellcasting:${level}`, pool: 'spellcasting', level,
      label: `Level ${level}`, detail: `${Number(regular[level]) || 0} left` })),
    ...levels(queries.availableSlots?.warlock).map((level) => ({ key: `pactMagic:${level}`, pool: 'pactMagic', level, label: `Pact (level ${level})`,
      detail: `${Number(queries.availableSlots.warlock[level]) || 0} left` })),
  ];
  const rowSpecs = [
    divine && { key: 'divine', label: LABEL, detail: fiendOrUndead ? 'Radiant · +1d8 against this Fiend or Undead' : 'Radiant', level: 1,
      text: (level) => `${dice(level)}d8`, typeLabel: 'Radiant', choiceId: (pool, level) => `slot:${pool}:${level}` },
    ...spellSmites.map((smite) => ({ key: smite.key, label: smite.name, detail: smite.note,
      level: smite.level, text: (level) => `${smite.dice(level)}d${smite.sides}`, typeLabel: smite.typeLabel, choiceId: (pool, level) => `${smite.key}:${pool}:${level}` })),
  ].filter(Boolean);
  const grid = columns.length ? {
    columns: columns.map(({ key, label, detail }) => ({ key, label, detail })),
    rows: rowSpecs.map((row) => ({ key: row.key, label: row.label, detail: row.detail, cells: Object.fromEntries(columns.map((column) => {
      if (column.level < row.level) return [column.key, null];
      const choiceId = row.choiceId(column.pool, column.level);
      const offered = choices.some((choice) => choice.id === choiceId);
      return [column.key, { label: row.text(column.level), ariaLabel: `${row.label} · ${column.label} slot · ${row.text(column.level)} ${row.typeLabel}`,
        ...(offered ? { choiceId } : { disabled: true, detail: current?.spellSlotSpent ? 'Only one spell slot can be spent per turn.' : 'No slots of this level left.' }) }];
    })) })),
  } : null;

  const offered = [...new Set(choices.map((choice) => (choice.spell === 'divine' ? LABEL : choice.spell.name)))];
  const title = offered.length > 1 ? 'Smite' : offered[0];
  const base = { featureId: DIVINE_SMITE_FEATURE_ID, featureLabel: title, reasonCode: 'melee-hit', components: [], claims: [] };
  // The grid rows say what each smite does; the prompt adds only what applies to this hit.
  const extras = [divine && fiendOrUndead ? 'Divine Smite deals +1d8 against this Fiend or Undead' : '', context.roll?.critical ? 'Dice are doubled for the critical hit' : ''].filter(Boolean);
  return {
    status: 'confirmation-required',
    ...base,
    decision: {
      id: `${context.resolutionId}:${DIVINE_SMITE_FEATURE_ID}`, kind: 'confirm-feature', featureId: DIVINE_SMITE_FEATURE_ID,
      resolutionId: context.resolutionId, title, label: title,
      prompt: offered.length === 1 && offered[0] === LABEL
        ? `Spend your Bonus Action to smite: Radiant damage${fiendOrUndead ? ', +1d8 against this Fiend or Undead' : ''}${context.roll?.critical ? ', doubled for the critical hit' : ''}.`
        : `Spend your Bonus Action to smite: pick ${offered.length > 1 ? 'a smite and ' : ''}a slot level.${extras.length ? ` ${extras.join('. ')}.` : ''}`,
      choices: [...choices.map(({ use, spell, ...choice }) => choice), { id: 'skip', label: 'No Smite' }],
      ...(grid ? { grid } : {}),
    },
    resolveDecision: (choiceId) => {
      const chosen = choices.find((choice) => choice.id === choiceId);
      if (!chosen) return base;
      const claim = { campaignId: combat.campaignId, combatantId: attackerId, featureId: DIVINE_SMITE_FEATURE_ID, turnSequence: combat.turnSequence,
        resolutionId: context.resolutionId, targetId: context.target?.combatantId };
      if (chosen.spell !== 'divine') {
        const smite = chosen.spell;
        const diceCount = smite.dice(chosen.use.slotLevel);
        return { ...base, featureLabel: smite.name, components: component(smite.name, diceCount, smite.sides, smite.type),
          claims: [{ ...claim, diceCount, spellId: smite.spellId, ...chosen.use }] };
      }
      const diceCount = dice(chosen.use.slotLevel);
      return { ...base, featureLabel: LABEL, components: component(LABEL, diceCount, 8, 'radiant'), claims: [{ ...claim, diceCount, ...chosen.use }] };
    },
  };
};
